import { toggleCheat, type CheatId } from "./cheats";
import type { InputFrame } from "./input";
import { HubList } from "./hubs";
import type { Tier } from "./ladder";
import { parsePick, parseStep, PauseMenu, type PauseAction, type SettingsScreen } from "./pause";
import {
  bindKey,
  bindPad,
  defaultSettings,
  RESERVED_KEYS,
  RESERVED_PAD,
  isListKey,
  stepChoice,
  stepTouchMode,
  stepTouchOpacity,
  stepTouchSize,
  stepTouchSteer,
  stepVolume,
  toggleTouchHand,
  type Action,
  type Settings,
} from "./settings";
import type { Cue } from "./sound/cues";
import { setSpecialSeq, specialSeqProblem, stepSpecialSeq, type Tap } from "./special-seqs";
import type { StatId } from "./stats";
import { DEFAULT_SPECIAL_SEQS, stickDir, type Dir } from "./tricks";

// Menu input while the sim holds still (#48): the pause menu, rebinding a
// button, a run's results screen and a challenge's cards. Kept free of the
// sim and of rendering so it can be tested; it says what should happen and
// Game does it.

/**
 * A special's row being set on Settings > Controls > Specials (#135):
 * waiting for its two stick taps (`taps`, the ones so far), or showing why
 * the last pair was refused (`taps` null, `note`).
 */
export interface SpecialEdit {
  name: string;
  taps: Tap[] | null;
  note: string | null;
}

/** Something the menus want done to the game. */
export type MenuIntent =
  /** Show or hide the Controls panel. */
  | { type: "help" }
  /** Use (and save) these settings. */
  | { type: "settings"; settings: Settings }
  /** Wait for a new button or key for this action, or stop waiting. */
  | { type: "rebind"; action: Action | null }
  /** Start, carry on or stop setting a special's taps (null: nothing being set). */
  | { type: "special"; edit: SpecialEdit | null }
  /** Spend a stat point on a stat, or take one back. */
  | { type: "stat"; id: StatId; up: boolean }
  | { type: "startRun" }
  | { type: "endRun" }
  | { type: "beginChallenge" }
  | { type: "retryChallenge" }
  | { type: "endChallenge" }
  /** Start this challenge from the hub's list (#86). */
  | { type: "pickChallenge"; id: string }
  /** Close the hub's list. */
  | { type: "closeHub" }
  /** From a challenge's pass / fail card, back to its tier's hub. */
  | { type: "toHub" }
  /** Pause > Challenges' "Go to hub" (#87): to an open tier's hub, facing its way. */
  | { type: "goHub"; tier: Tier }
  | { type: "startDemo" }
  | { type: "stopDemo" }
  | { type: "map"; id: string }
  /** Ride this skater (#64). */
  | { type: "skater"; id: string }
  /** Back to the spawn (ending a demo). */
  | { type: "reset" }
  /** Back to the lobby (#224): whatever's on ends, and the menu closes with it. */
  | { type: "lobby" }
  /** Ignore the ollie button until it's let go, so leaving a screen with X doesn't pop an ollie. */
  | { type: "swallowOllie" }
  /** Close the pause menu and pick the sim back up: the ollie swallowed, the clock started over. */
  | { type: "close" };

/** One frame of the menus: sounds to play, then intents to carry out, in order. */
export interface MenuStep {
  cues: Cue[];
  intents: MenuIntent[];
}

/** What the menus need to know about the game. */
export interface MenuContext {
  /** The skater's started (past the start screen) and no map is loading. */
  live: boolean;
  /** A 2-minute run is on (its results screen too). */
  inRun: boolean;
  /** Its results screen is up. */
  results: boolean;
  /** A challenge's start card ("ready") or its pass / fail card ("over") is up, or null. */
  card: "ready" | "over" | null;
  /** The map has challenge hubs (#86): the pass / fail card can send you back to one. */
  hubs: boolean;
  /** The open hubs' tiers (#87), for Pause > Challenges' "Go to hub" rows; none without hubs. */
  openHubs: readonly Tier[];
  /** Demo mode is playing. */
  demo: boolean;
  mapId: string;
  /** Who's being ridden. */
  skaterId: string;
  settings: Readonly<Settings>;
  /** The action waiting for its new button or key, or null. */
  rebinding: Action | null;
  /** A special's taps being set, or its refusal showing (#135), or null. */
  special: SpecialEdit | null;
  /** The pad button index and keyboard code first pressed this frame, if any. */
  padPress: number | null;
  keyPress: string | null;
}

/** Start on a standard pad. */
const PAD_START = 9;

export class MenuController {
  readonly pause = new PauseMenu();
  /** A hub's list of challenges (#86). */
  readonly hub = new HubList();
  /** The highlight last frame the menu ran, or -1: a move clicks, opening doesn't. */
  private lastIndex = -1;
  /** Where the stick was last frame while waiting for special taps; null on the first frame. */
  private lastTap: Dir | null = null;

  /**
   * One frame of input while the sim holds still: the pause menu (or the
   * binding it's waiting on), else a hub's list, the results screen or a
   * challenge card.
   */
  frame(input: InputFrame, ctx: MenuContext): MenuStep {
    if (this.pause.open && ctx.rebinding) return this.captureBinding(ctx.rebinding, ctx);
    if (this.pause.open && ctx.special?.taps) return this.captureSpecial(input, ctx.special, ctx);
    const cues: Cue[] = [];
    if (this.pause.open) {
      const action = this.pause.handle(input);
      if (this.pause.index !== this.lastIndex && this.lastIndex >= 0) cues.push("menu");
      this.lastIndex = this.pause.index;
      if (!action) return { cues, intents: [] };
      cues.push("pick");
      return { cues, intents: this.act(action, ctx) };
    }
    if (!ctx.live) return { cues, intents: [] };
    if (this.hub.open) {
      const before = this.hub.hud!.index;
      const pick = this.hub.handle(input);
      if (this.hub.hud && this.hub.hud.index !== before) cues.push("menu");
      if (pick?.type === "pick") return { cues: ["pick"], intents: [{ type: "pickChallenge", id: pick.id }] };
      if (pick?.type === "close") return { cues: ["menu"], intents: [{ type: "closeHub" }, { type: "swallowOllie" }] };
      return { cues, intents: [] };
    }
    const { pressed } = input;
    if (ctx.results) {
      // X skates the map again; O or Start goes back to free skate.
      if (pressed.ollie) return { cues, intents: [{ type: "startRun" }] };
      if (pressed.grab || pressed.pause) return { cues, intents: [{ type: "endRun" }, { type: "swallowOllie" }] };
    } else if (ctx.card) {
      // Start card: X goes, O or Start backs out. Pass / fail: X again, O or Start back to free skate, △ back to the hub.
      if (pressed.ollie) return { cues: ["pick"], intents: [{ type: ctx.card === "ready" ? "beginChallenge" : "retryChallenge" }] };
      if (pressed.grind && ctx.card === "over" && ctx.hubs) return { cues: ["pick"], intents: [{ type: "toHub" }] };
      if (pressed.grab || pressed.pause) return { cues: ["pick"], intents: [{ type: "endChallenge" }] };
    }
    return { cues, intents: [] };
  }

  /** The sim ran this frame: the next time the menu opens, its first highlight doesn't click. */
  simRan(): void {
    this.lastIndex = -1;
  }

  /**
   * A pause menu item picked (with the pad, or clicked): moves between the
   * menu's screens itself and says what the game should do.
   */
  act(action: PauseAction, ctx: MenuContext): MenuIntent[] {
    // Whatever's picked next, a special's refusal note has been read.
    const intents = this.actOn(action, ctx);
    return ctx.special ? [{ type: "special", edit: null }, ...intents] : intents;
  }

  private actOn(action: PauseAction, ctx: MenuContext): MenuIntent[] {
    const settings = (s: Settings): MenuIntent[] => [{ type: "settings", settings: s }];
    switch (action) {
      case "controls":
        return [{ type: "help" }];
      case "settings":
        this.pause.openSettings();
        return [];
      case "volume+":
      case "volume-":
        return settings(stepVolume(ctx.settings, action === "volume+" ? 1 : -1));
      case "cheats":
        this.pause.openCheats();
        return [];
      case "touchMode+":
      case "touchMode-":
        return settings(stepTouchMode(ctx.settings, action === "touchMode+" ? 1 : -1));
      case "touchSize+":
      case "touchSize-":
        return settings(stepTouchSize(ctx.settings, action === "touchSize+" ? 1 : -1));
      case "touchSteer+":
      case "touchSteer-":
        return settings(stepTouchSteer(ctx.settings, action === "touchSteer+" ? 1 : -1));
      case "touchOpacity+":
      case "touchOpacity-":
        return settings(stepTouchOpacity(ctx.settings, action === "touchOpacity+" ? 1 : -1));
      case "touchHand":
        return settings(toggleTouchHand(ctx.settings));
      case "specials":
        this.pause.openSpecials();
        return [];
      case "resetSpecials":
        return settings({ ...ctx.settings, specials: DEFAULT_SPECIAL_SEQS });
      case "resetControls": {
        const d = defaultSettings();
        return settings({ ...ctx.settings, pad: d.pad, keys: d.keys });
      }
      case "maps":
        this.pause.openMaps(ctx.mapId);
        return [];
      case "gaps":
        this.pause.openGaps();
        return [];
      case "news":
        this.pause.openNews();
        return [];
      case "goals":
        this.pause.openGoals(ctx.openHubs);
        return [];
      case "stats":
        this.pause.openStats();
        return [];
      case "skater":
        this.pause.openSkater(ctx.skaterId);
        return [];
      case "back":
        this.pause.back();
        return [];
      case "run":
        // Starting a run closes the menu itself.
        return ctx.inRun ? [{ type: "endRun" }, { type: "close" }] : [{ type: "startRun" }];
      case "retry":
        return [{ type: "retryChallenge" }, { type: "close" }];
      case "quit":
        return [{ type: "endChallenge" }, { type: "close" }];
      case "demo":
        return [{ type: ctx.demo ? "stopDemo" : "startDemo" }, { type: "close" }];
      case "reset":
        return [{ type: "reset" }, { type: "close" }];
      case "resume":
        return [{ type: "close" }];
      case "lobby":
        return [{ type: "lobby" }];
    }
    // A pick-one setting's row (`SETTING_ROWS`). A stepped one steps (X or →
    // up, ← down); a list one (art style, quality) opens a list of every
    // choice: moving through it rebuilds nothing, picking one applies it.
    if (action.startsWith("group:")) {
      this.pause.openGroup(action.slice("group:".length) as SettingsScreen);
      return [];
    }
    if (isListKey(action)) {
      this.pause.openList(action, ctx.settings[action]);
      return [];
    }
    const step = parseStep(action);
    if (step) return settings(stepChoice(ctx.settings, step.key, step.dir));
    // A pick in a list goes back to Settings; a new choice is one settings change (one rebuild), the current one none.
    const pick = parsePick(action);
    if (pick) {
      this.pause.back();
      return pick.id === ctx.settings[pick.key] ? [] : settings({ ...ctx.settings, [pick.key]: pick.id });
    }
    // A cheat flips where it is: the list stays open to set the rest.
    if (action.startsWith("cheat:")) return settings({ ...ctx.settings, cheats: toggleCheat(ctx.settings.cheats, action.slice("cheat:".length) as CheatId) });
    // A special's row: with keys or a pad, X waits for its two stick taps; on glass it steps like ←/→.
    if (action.startsWith("special:")) {
      const name = action.slice("special:".length);
      if (!this.pause.controls.buttons) return settings({ ...ctx.settings, specials: stepSpecialSeq(ctx.settings.specials, name, 1) });
      this.lastTap = null;
      return [{ type: "special", edit: { name, taps: [], note: null } }];
    }
    if (action.startsWith("special+:") || action.startsWith("special-:")) {
      const name = action.slice("special+:".length);
      return settings({ ...ctx.settings, specials: stepSpecialSeq(ctx.settings.specials, name, action[7] === "+" ? 1 : -1) });
    }
    if (action.startsWith("bind:")) return [{ type: "rebind", action: action.slice("bind:".length) as Action }];
    if (action.startsWith("hub:")) return [{ type: "goHub", tier: Number(action.slice("hub:".length)) as Tier }, { type: "close" }];
    // A day of What's new is only there to scroll to.
    if (action.startsWith("news:")) return [];
    if (action.startsWith("map:")) return [{ type: "map", id: action.slice("map:".length) }, { type: "close" }];
    // Picking a skater swaps them in behind the menu, which stays open to compare.
    if (action.startsWith("skater:")) return [{ type: "skater", id: action.slice("skater:".length) }];
    const [verb, id] = action.split(":") as ["stat" | "unstat", StatId];
    return [{ type: "stat", id, up: verb === "stat" }];
  }

  /**
   * While a special waits for its taps (#135): each new stick direction is a
   * tap; the second sets the pair, unless a pop or another special on that
   * button has it, which leaves the old one and says why. Start, Esc or O
   * cancels. The menu doesn't move meanwhile.
   */
  private captureSpecial(input: InputFrame, edit: SpecialEdit, ctx: MenuContext): MenuStep {
    this.pause.holdStick(input);
    if (input.pressed.pause || input.pressed.grab || ctx.keyPress === "Escape") return { cues: ["menu"], intents: [{ type: "special", edit: null }] };
    const d = stickDir(input.moveX, input.moveY);
    const last = this.lastTap;
    this.lastTap = d;
    // The first frame only notes where the stick is, so one held from before isn't a tap.
    if (last === null || d === "n" || d === last) return { cues: [], intents: [] };
    const taps = [...edit.taps!, d];
    if (taps.length < 2) return { cues: ["menu"], intents: [{ type: "special", edit: { ...edit, taps } }] };
    const seq: [Tap, Tap] = [taps[0]!, taps[1]!];
    const problem = specialSeqProblem(ctx.settings.specials, edit.name, seq);
    if (problem) return { cues: ["menu"], intents: [{ type: "special", edit: { name: edit.name, taps: null, note: problem } }] };
    return {
      cues: ["pick"],
      intents: [
        { type: "settings", settings: { ...ctx.settings, specials: setSpecialSeq(ctx.settings.specials, edit.name, seq) } },
        { type: "special", edit: null },
      ],
    };
  }

  /**
   * While a binding waits for its button: the first pad button or key
   * pressed takes the action (a reserved one is ignored); Start or Esc
   * cancels. The menu doesn't move meanwhile.
   */
  private captureBinding(a: Action, ctx: MenuContext): MenuStep {
    const { padPress: pad, keyPress: key } = ctx;
    if (pad === PAD_START || key === "Escape") return { cues: ["menu"], intents: [{ type: "rebind", action: null }] };
    let s: Settings;
    if (pad !== null && !RESERVED_PAD.includes(pad)) s = bindPad(ctx.settings, a, pad);
    else if (key !== null && !RESERVED_KEYS.includes(key)) s = bindKey(ctx.settings, a, key);
    else return { cues: [], intents: [] };
    return { cues: ["pick"], intents: [{ type: "settings", settings: s }, { type: "rebind", action: null }] };
  }
}
